import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import {
  Users,
  ArrowRightLeft,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
  ChevronRight,
  Search,
  AlertCircle,
  X,
  MessageSquare
} from 'lucide-react';

const Partners = () => {
  const queryClient = useQueryClient();
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    notes: '',
  });
  const [isPayoutOpen, setIsPayoutOpen] = useState(false);
  const [payoutData, setPayoutData] = useState({
    wallet_id: '',
    user_id: '', // Empty means ALL
    amount: '',
    notes: 'Выплата дивидендов',
  });

  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await client.get('/dashboard')).data
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await client.get('/users')).data
  });

  // Filter partners (excluding 'admin' by name if needed, but 'admin' role might be a partner too)
  // Logic: Users with role 'partner' OR 'admin' (if they participate in finance) AND have at least one wallet
  const partners = users?.filter(u => ['partner', 'admin'].includes(u.role) && u.name !== 'admin' && dashboardData?.wallets?.some(w => w.owner_id === u.id)) || [];

  const transferMutation = useMutation({
    mutationFn: (data) => client.post('/transfer', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['dashboard']);
      setIsTransferOpen(false);
      setTransferData({ from_wallet_id: '', to_wallet_id: '', amount: '', notes: '' });
      alert('Перевод успешно выполнен');
    }
  });

  const payoutMutation = useMutation({
    mutationFn: (data) => client.post('/partners/payout', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['dashboard']);
      setIsPayoutOpen(false);
      setPayoutData({ wallet_id: '', user_id: '', amount: '', notes: 'Выплата дивидендов' });
      alert('Дивиденды успешно выплачены');
    }
  });

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await transferMutation.mutateAsync(transferData);
    } catch (err) {
      alert('Ошибка при переводе: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    try {
      await payoutMutation.mutateAsync(payoutData);
    } catch (err) {
      alert('Ошибка при выплате: ' + (err.response?.data?.detail || err.message));
    }
  };

  // --- EQUALIZATION LOGIC (v2: Accounting for Dividends) ---
  const dividendsPerUser = dashboardData?.dividends_per_user || {};
  const totalDividendsAllTime = Object.values(dividendsPerUser).reduce((a, b) => a + Number(b), 0);
  const totalSystemCapital = dashboardData?.total_balance || 0;
  const totalAdjustedCapital = totalSystemCapital + totalDividendsAllTime;

  // Calculate Equalization
  const ecosystem = partners.map(user => {
    const userWallets = dashboardData?.wallets?.filter(w => w.owner_id === user.id) || [];
    const walletBalance = userWallets.reduce((sum, w) => sum + w.balance, 0);
    const dividendsTaken = Number(dividendsPerUser[user.id] || 0);

    // Equity = what participant has in wallets + what they already took out
    const currentEquity = walletBalance + dividendsTaken;
    const sharePercent = user.profit_percentage || 0;

    // Target Equity = Everything earned (Liquid + Paid out) * Share %
    const targetEquity = totalAdjustedCapital * (sharePercent / 100);

    // Diff: + means User has taken/held more than their share (Owes), - means less (Owed)
    const diff = currentEquity - targetEquity;

    return {
      user,
      walletBalance,
      dividendsTaken,
      currentEquity,
      sharePercent,
      targetEquity,
      diff,
      userWallets
    };
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tighter uppercase">Взаиморасчеты</h1>
          <p className="text-neutral-500 text-[10px] font-black uppercase tracking-[0.2em] mt-2">Баланс партнеров и переводы</p>
        </div>
        <div className="flex flex-wrap gap-4 w-full md:w-auto">
          <button
            onClick={() => setIsTransferOpen(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-neutral-900 border border-neutral-800 text-white px-8 py-4 rounded-3xl font-black uppercase text-xs tracking-widest hover:border-blue-500 transition-all shadow-xl active:scale-95"
          >
            <ArrowRightLeft className="w-5 h-5 text-blue-500" />
            Перевод
          </button>
          <button
            onClick={() => setIsPayoutOpen(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-emerald-600 text-white px-8 py-4 rounded-3xl font-black uppercase text-xs tracking-widest hover:bg-emerald-500 transition-all shadow-xl active:scale-95"
          >
            <ArrowUpRight className="w-5 h-5" />
            Выплата дивидендов
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {ecosystem.map(({ user, walletBalance, dividendsTaken, currentEquity, sharePercent, diff, userWallets }) => {
          const isOwed = diff < -1; // Tolerance of 1 unit
          const isOwing = diff > 1;
          const statusColor = isOwing ? 'text-rose-500' : isOwed ? 'text-emerald-500' : 'text-neutral-500';
          const statusBg = isOwing ? 'bg-rose-500/10' : isOwed ? 'bg-emerald-500/10' : 'bg-neutral-800';

          return (
            <div key={user.id} className="bg-neutral-900 border border-neutral-800 p-8 rounded-[40px] shadow-2xl relative overflow-hidden group hover:border-neutral-700 transition-all">
              <div className="absolute top-0 right-0 p-8">
                <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${user.role === 'admin' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500'}`}>
                  {sharePercent}% Доля
                </span>
              </div>

              <div className="w-16 h-16 bg-neutral-800 rounded-3xl flex items-center justify-center text-2xl font-black text-white mb-6 border border-neutral-700 shadow-lg group-hover:bg-blue-600 transition-all duration-500 group-hover:rotate-6">
                {user.name[0].toUpperCase()}
              </div>

              <h3 className="text-xl font-black text-white mb-1 uppercase tracking-tight">{user.name}</h3>
              <p className="text-xs text-neutral-500 font-bold uppercase tracking-widest mb-8">{user.fio || 'Партнер'}</p>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-[9px] font-black text-neutral-600 uppercase tracking-widest mb-1">В кошельках</p>
                    <p className="text-sm font-black text-white whitespace-nowrap">{walletBalance.toLocaleString()} ₽</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-black text-neutral-600 uppercase tracking-widest mb-1">Дивиденды (всего)</p>
                    <p className="text-sm font-black text-emerald-500 whitespace-nowrap">{dividendsTaken.toLocaleString()} ₽</p>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-end mb-2">
                    <p className="text-[10px] font-black text-neutral-500 uppercase tracking-widest">Текущий капитал (Equity)</p>
                    <p className="text-lg font-black text-white whitespace-nowrap">{currentEquity.toLocaleString()} ₽</p>
                  </div>
                  <div className="w-full h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min((currentEquity / totalAdjustedCapital) * 100 || 0, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className={`p-5 rounded-3xl border border-dashed border-neutral-700 ${statusBg}`}>
                  <p className="text-[10px] font-black text-neutral-400 uppercase tracking-widest mb-1">
                    {isOwing
                      ? 'НУЖНО ВЕРНУТЬ В ОБОРОТ'
                      : isOwed
                        ? 'НУЖНО ДОПЛАТИТЬ В ОБОРОТ'
                        : 'БАЛАНС В НОРМЕ'}
                  </p>
                  <p className={`text-2xl font-black ${statusColor}`}>
                    {Math.abs(diff).toLocaleString()} ₽
                  </p>
                </div>

                {/* Equalization Block */}
                {(() => {
                  const otherPartner = ecosystem.find(p => p.user.id !== user.id);
                  if (otherPartner) {
                    // We rely on 'diff' calculated based on profit share
                    // diff > 0 means User has Excess (should pay)
                    // diff < 0 means User has Deficit (should receive)

                    const equalizationAmount = Math.abs(diff);
                    const isPayer = diff > 0;

                    // Display logic:
                    // If I am Payer (diff > 0): "I owe Partner"
                    // If I am Payee (diff < 0): "Partner owes Me"

                    const eqColor = isPayer ? 'text-amber-500' : 'text-blue-500';
                    const eqBg = isPayer ? 'bg-amber-500/10' : 'bg-blue-500/10';

                    if (equalizationAmount > 10) { // Tolerance
                      return (
                        <div className={`p-5 rounded-3xl border border-dashed border-neutral-700 ${eqBg} mt-4`}>
                          <p className="text-[10px] font-black text-neutral-400 uppercase tracking-widest mb-1">
                            ДЛЯ ВЫРАВНИВАНИЯ ДОЛЕЙ
                          </p>
                          <p className={`text-xl font-black ${eqColor} mb-1`}>
                            {equalizationAmount.toLocaleString()} ₽
                          </p>
                          <p className="text-[10px] font-bold text-neutral-300">
                            {isPayer
                              ? `${user.name.toUpperCase()} → ОТДАЙ ПАРТНЕРУ`
                              : `${otherPartner.user.name.toUpperCase()} → ВАМ ДОЛЖНЫ`}
                          </p>
                          <p className="text-[9px] text-neutral-500 mt-2 leading-tight">
                            * {isPayer ? 'Вы должны перевести партнеру' : 'Партнер должен перевести вам'}, чтобы соблюсти доли ({sharePercent}% / {otherPartner.sharePercent}%).
                          </p>
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {userWallets.map(w => (
                  <span key={w.id} className="px-3 py-1 bg-neutral-800 rounded-lg text-[9px] font-black text-neutral-400 border border-neutral-700 uppercase tracking-tight">
                    {w.name}: {w.balance.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {isPayoutOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 border border-neutral-800 w-full max-w-xl rounded-3xl md:rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 md:p-10">
              <div className="flex justify-between items-center mb-8">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight">Выплата дивидендов</h3>
                <button onClick={() => setIsPayoutOpen(false)} className="p-2 hover:bg-neutral-800 rounded-full transition-all text-neutral-500 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handlePayout} className="space-y-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Источник (Кошелек)</label>
                  <select
                    required
                    value={payoutData.wallet_id}
                    onChange={(e) => setPayoutData({ ...payoutData, wallet_id: e.target.value })}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-5 rounded-3xl outline-none focus:border-emerald-500 transition-all font-bold"
                  >
                    <option value="">Выберите кошелек...</option>
                    {dashboardData?.wallets?.map(w => <option key={w.id} value={w.id}>{w.name} ({w.balance.toLocaleString()} ₽)</option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Получатель</label>
                  <select
                    value={payoutData.user_id}
                    onChange={(e) => setPayoutData({ ...payoutData, user_id: e.target.value })}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-5 rounded-3xl outline-none focus:border-emerald-500 transition-all font-bold"
                  >
                    <option value="">Всем партнерам (согласно долям)</option>
                    {partners.map(p => (
                      <option key={p.id} value={p.id}>{p.fio || p.name} ({p.share_percent}%)</option>
                    ))}
                  </select>
                  <p className="text-[9px] text-neutral-500 font-bold uppercase tracking-tight ml-1">
                    * {payoutData.user_id ? 'Вся сумма будет выплачена одному партнеру' : 'Сумма будет разделена между партнерами пропорционально их долям'}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Сумма к выплате</label>
                  <input
                    type="number"
                    required
                    value={payoutData.amount}
                    onChange={(e) => setPayoutData({ ...payoutData, amount: e.target.value })}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-6 rounded-3xl outline-none focus:border-emerald-500 transition-all font-black text-3xl"
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Комментарий</label>
                  <textarea
                    value={payoutData.notes}
                    onChange={(e) => setPayoutData({ ...payoutData, notes: e.target.value })}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-5 rounded-3xl outline-none focus:border-emerald-500 transition-all font-bold h-24 resize-none"
                    placeholder="Дивиденды за март..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={payoutMutation.isPending}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase text-xs tracking-[0.3em] py-6 rounded-[40px] transition-all shadow-xl shadow-emerald-600/30 active:scale-[0.98] disabled:opacity-50"
                >
                  {payoutMutation.isPending ? 'ВЫПОЛНЯЕТСЯ...' : 'ВЫПЛАТИТЬ ДИВИДЕНДЫ'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      {isTransferOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 border border-neutral-800 w-full max-w-xl rounded-3xl md:rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[100dvh] flex flex-col">
            <div className="p-6 md:p-10 overflow-y-auto custom-scrollbar">
              <div className="flex justify-between items-center mb-8 sticky top-0 bg-neutral-900/95 backdrop-blur z-10 py-2 -mt-2">
                <h3 className="text-2xl font-black text-white uppercase tracking-tight">Перевод средств</h3>
                <button onClick={() => setIsTransferOpen(false)} className="p-2 hover:bg-neutral-800 rounded-full transition-all text-neutral-500 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleTransfer} className="space-y-8 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Откуда</label>
                    <select
                      required
                      value={transferData.from_wallet_id}
                      onChange={(e) => setTransferData({ ...transferData, from_wallet_id: e.target.value })}
                      className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-5 rounded-3xl outline-none focus:border-blue-500 transition-all font-bold"
                    >
                      <option value="">Списать с...</option>
                      {dashboardData?.wallets?.map(w => <option key={w.id} value={w.id}>{w.name} ({w.balance.toLocaleString()} ₽)</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Куда</label>
                    <select
                      required
                      value={transferData.to_wallet_id}
                      onChange={(e) => setTransferData({ ...transferData, to_wallet_id: e.target.value })}
                      className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-5 rounded-3xl outline-none focus:border-blue-500 transition-all font-bold"
                    >
                      <option value="">Зачислить на...</option>
                      {dashboardData?.wallets?.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Сумма перевода</label>
                  <input
                    type="number"
                    required
                    value={transferData.amount}
                    onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                    className="w-full bg-neutral-800 border-2 border-neutral-700 text-white p-6 rounded-3xl outline-none focus:border-blue-500 transition-all font-black text-3xl"
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest ml-1">Заметка к переводу</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-5 top-5 w-5 h-5 text-neutral-600" />
                    <textarea
                      value={transferData.notes}
                      onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                      className="w-full bg-neutral-800 border-2 border-neutral-700 text-white pl-14 pr-5 py-5 rounded-3xl outline-none focus:border-blue-500 transition-all font-bold h-24 resize-none"
                      placeholder="Напр: Вывод дивидендов..."
                    />
                  </div>
                </div>

                <div className="sticky bottom-0 bg-neutral-900/95 backdrop-blur py-4 -mb-4">
                  <button
                    type="submit"
                    disabled={transferMutation.isPending}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black uppercase text-xs tracking-[0.3em] py-6 rounded-[40px] transition-all shadow-xl shadow-blue-600/30 active:scale-[0.98] disabled:opacity-50"
                  >
                    {transferMutation.isPending ? 'ВЫПОЛНЯЕТСЯ...' : 'ПОДТВЕРДИТЬ ПЕРЕВОД'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Partners;
